<template>
  <div>
    <popper
      v-if="isResponsible"
      ref="popper"
      trigger="clickToToggle"
      :append-to-body="true"
      :visible-arrow="false"
      root-class="profile-user-popper"
      :options="popperOptions"
    >
      <div class="profile-menu profile-user-nav">
        <article>
          <h4 class="text-left">{{ $t("pages.users.my_profile") }}</h4>
          <div class="row">
            <div class="col text-center">
              <div
                class="user-type-icon user-type-icon-left"
                @click="setEmployeeRole"
                :class="{ 'user-type-no-active': isCurrentRoleResponsible }"
              >
                <img src="@/assets/IC_Empleado.png" alt="image" class="user-icon" />
              </div>
              <p class="user-type">{{ $t("pages.users.employee") }}</p>
            </div>
            <div class="col text-center">
              <div
                class="user-type-icon user-type-icon-right"
                @click="setResponsibleRole"
                :class="{ 'user-type-no-active': isCurrentRoleEmployee }"
              >
                <img src="@/assets/IC_Jefe.png" alt="image" class="user-icon" />
              </div>
              <p class="user-type">{{ $t("pages.users.boss") }}</p>
            </div>
          </div>
          <div class="row py-3">
            <div class="col text-right">
              <a class="profile-link" href="#" @click="toProfilePage">
                {{ $t("pages.users.see_my_data") }}
                <i class="fas fa-chevron-right" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </article>
      </div>

      <div slot="reference">
        <button type="button" class="name-user name-user--button">
          <span class="name-user__label">
            <span class="name-user__name">{{ userName }}</span>
            <span class="name-user__sep">·</span>
            <span class="name-user__role">
              <template v-if="isCurrentRoleEmployee">{{ $t("pages.users.employee") }}</template>
              <template v-else>{{ $t("pages.users.boss") }}</template>
            </span>
          </span>
          <i class="fas fa-chevron-down name-user__chevron" aria-hidden="true"></i>
        </button>
      </div>
    </popper>

    <div class="name-user name-user--link" v-else>
      <router-link :to="{ name: 'profile' }">
        <span>{{ userName }}</span>
      </router-link>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Popper from "vue-popperjs";
import "vue-popperjs/dist/vue-popper.css";

export default {
  name: "ProfileUserMenu",
  components: {
    Popper,
  },
  computed: {
    ...mapGetters("auth", [
      "userName",
      "isResponsible",
      "isCurrentRoleResponsible",
      "isCurrentRoleEmployee",
      "showEmployeePersonalData",
    ]),
    popperOptions() {
      return {
        placement: "bottom-end",
        modifiers: {
          preventOverflow: {
            enabled: true,
            boundariesElement: "viewport",
            padding: 8,
          },
          hide: {
            enabled: false,
          },
        },
      };
    },
  },
  methods: {
    ...mapActions("auth", ["changeToResponsibleRole", "changeToEmployee"]),
    toProfilePage(event) {
      if (this.isCurrentRoleEmployee && !this.showEmployeePersonalData) return;
      event.preventDefault();
      this.$refs.popper.doClose();
      this.$router.push({ name: "profile" });
    },
    setResponsibleRole() {
      this.changeToResponsibleRole();
      this.toHome();
    },
    setEmployeeRole() {
      this.changeToEmployee();
      this.toHome();
    },
    toHome() {
      const route = this.$router.currentRoute;
      if (route.name != "dashboard") {
        this.$router.push({ name: "dashboard" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.profile-menu {
  position: relative;
  z-index: 1045;
  margin-top: 8px;
}

.name-user--button {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  max-width: 260px;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--portal-border);
  border-radius: 10px;
  background: var(--portal-surface);
  color: var(--portal-text);
  box-shadow: var(--portal-shadow-sm);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;

  &:hover {
    background: var(--portal-surface-muted);
    border-color: transparent;
    box-shadow: var(--portal-shadow);
  }

  &:focus {
    outline: 2px solid var(--portal-ring);
    outline-offset: 2px;
  }
}

.name-user__label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.name-user__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.name-user__sep {
  color: var(--portal-text-muted);
}

.name-user__role {
  color: var(--portal-text-muted);
  font-weight: 600;
}

.name-user__chevron {
  flex-shrink: 0;
  font-size: 0.68rem;
  color: var(--portal-text-muted);
}

.name-user--link {
  a,
  span {
    color: var(--portal-text) !important;
    font-size: 0.86rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.3;
    text-decoration: none;
  }

  a:hover {
    color: var(--portal-primary) !important;
  }
}

.profile-user-nav {
  width: 340px;
  overflow: hidden;
  border: 1px solid var(--portal-border);
  border-radius: 16px;
  background-color: var(--portal-surface);
  color: var(--portal-text);
  box-shadow: var(--portal-shadow);

  & article > h4 {
    margin: 0;
    padding: 1rem 1.1rem 0.75rem;
    color: var(--portal-text);
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  & .user-icon {
    width: 110px;
    height: 110px;
    object-fit: contain;
  }

  & .user-type {
    color: var(--portal-text-muted);
    font-size: 0.88rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.3;
    text-align: center;
    padding-top: 0.75rem;
  }

  .user-type-icon {
    display: inline-flex;
    border: 1px solid var(--portal-border);
    border-radius: 14px;
    background: var(--portal-surface-muted);
    box-shadow: none;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      border-color: transparent;
      box-shadow: var(--portal-shadow);
    }
  }

  .user-type-no-active {
    opacity: 0.35;
    border-radius: 14px;
    background-color: var(--portal-surface-muted);
    box-shadow: none;
  }

  $margin-user-type: 12px;

  .user-type-icon-left {
    margin-left: $margin-user-type;
  }

  .user-type-icon-right {
    margin-right: $margin-user-type;
  }

  .profile-link {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0 1.1rem 0.25rem;
    color: var(--portal-primary);
    font-size: 0.9rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.3;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    i {
      font-size: 0.72rem;
    }
  }
}
</style>
